import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faTrash, faUsers } from "@fortawesome/free-solid-svg-icons";
import api from "../../../services/api";
import toastOptions from "../../../utils/toastOptions";
import ICoupon from "../../../models/ICoupon";
import CouponAdd from "../coupon-add/CouponAdd";
import { authenticationService } from "../../../services/auth.service";
import { currencyMask, displayDate, showCouponScope, showCouponType } from "../../../utils/masks";
import { TablePage } from "../../../containers";
import moment from "../../../utils/momentConfig";
import { StatusType } from "../../../models/enums/StatusType";
import DialogConfirm from "../../../components/DialogConfirm";
import { CouponType } from "../../../models/enums/CouponType";
import { useNavigate } from "react-router-dom";

interface CouponListProps {
  idScope?: number;
  scope?: string;
}

export default function CouponList(props: CouponListProps) {
  const navigate = useNavigate();
  const [refresh, setRefresh] = useState<boolean>(false);
  const [showAddEdit, setShowAddEdit] = useState<boolean>(false);
  const [couponEdit, setCouponEdit] = useState<ICoupon>();
  const [pageNo, setPageNo] = useState<number>(1);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  var couponSelected = useRef<ICoupon | null>(null);
  const [ixLoading, setIxLoading] = useState<number>(-1);

  const today = moment();

  const columns = [
    {
      name: "Código",
      sortable: true,
      cell: (row: ICoupon) => <span className="uk-text-bold">{row.code}</span>
    },
    {
      name: "Escopo",
      sortable: true,
      omit: !!props.scope,
      cell: (row: ICoupon) => <span>{showCouponScope(row.scope)}</span>
    },
    {
      name: "Informações",
      sortable: true,
      grow: 2,
      cell: (row: ICoupon) => <div className="uk-flex uk-flex-column">
        <span>{showCouponType(row.type)} de <span className="uk-text-bold uk-text-muted">{row.type === CouponType.VALUE ? currencyMask(row.value) : `${row.value}%`}</span></span>
        {row.status == StatusType.INACTIVE && <span className="text-nowrap uk-text-italic">Desativado em {displayDate(row?.modifiedDate?.toString())}</span>}
        {row.status != StatusType.INACTIVE && <span className="text-nowrap uk-text-italic">Válido de {displayDate(row?.startsAt?.toString())} até {displayDate(row?.expiresAt?.toString())}</span>}
        {(row.redemptionsCount > 0 || (row.limitUse ?? 0) > 0) && 
          <span className="text-nowrap uk-text-italic uk-text-small">
            {row.redemptionsCount} utilizado(s){row.redemptionsCount > 1 ? "s" : ""} {row.limitUse ? `de ${row.limitUse}` : ""}
          </span>}
      </div>
    },
    {
      name: "Status",
      sortable: false,
      cell: (row: ICoupon) => <>
        {handleStatusColumn(row)}
      </>
    },
    {
      name: "Ações",
      button: true,
      sortable: false,
      omit: authenticationService.isReadOnly(),
      cell: (row: ICoupon) => <>
          <div className="uk-flex">
            {authenticationService.isFinance() &&
              <button
                type="button"
                onClick={(ev) => {
                  ev.preventDefault();
                  navigate(`/cupons/${row.id}/pessoas`, {
                    state: {
                      couponCode: row.code
                    }
                  });
                }}
                className="uk-button uk-button-link uk-padding-remove uk-margin-small-right"
                uk-tooltip="title: Ver pessoas que resgataram este cupom"
              >
                <FontAwesomeIcon icon={faUsers} />
              </button>
            }
            {(row.status == StatusType.ACTIVE && moment(row.expiresAt).isSameOrAfter(today)) && <>
            <button
              type="button"
              onClick={(ev) => {
                ev.preventDefault();
                handleEdit(row);
              }}
              className="uk-button uk-button-link uk-padding-remove uk-margin-small-right"
              uk-tooltip="title: Editar"
            >
              <FontAwesomeIcon icon={faPencil} />
            </button>
            <button
              type="button"
              onClick={(ev) => {
                ev.preventDefault();
                handleRemove(row);
              }}
              className="uk-button uk-button-link uk-padding-remove"
              uk-tooltip="title: Desativar"
            >
              {ixLoading === row.id ? <span uk-spinner="ratio: 0.5"></span> : <FontAwesomeIcon icon={faTrash} />}
            </button>
            </>}
          </div>
      </>
    }
  ];

  const handleStatusColumn = (row: ICoupon) => {
    if (row.status == StatusType.INACTIVE) {
      return <span className={`uk-badge uk-badge-danger uk-text-nowrap`}>Cancelado</span>
    }

    if (moment(row.expiresAt).isBefore(today)) {
      return <span className={`uk-badge uk-badge-warning uk-text-nowrap`}>Expirado</span>
    }
    
    if (row.redemptionsCount > 0 && row.limitUse && row.redemptionsCount >= row.limitUse) {
      return <span className={`uk-badge uk-badge-primary uk-text-nowrap`}>Esgotado</span>
    }

    return <span className={`uk-badge uk-badge-success uk-text-nowrap`}>Ativo</span>
  }

  const handleNew = () => {
    setCouponEdit(undefined);
    setShowAddEdit(true);
  };

  const handleEdit = (coupon: ICoupon) => {
    setCouponEdit(coupon);
    setShowAddEdit(true);
  };

  const handleSaved = () => {
    setShowAddEdit(false);
    setCouponEdit(undefined);
    setRefresh(!refresh);
  };

  const handleCancel = () => {
    setShowAddEdit(false);
    setCouponEdit(undefined);
  };

  const handleRemove = async (row: ICoupon) => {
    couponSelected.current = row;
    setIsDialogOpen(true);
  };

  const dialogClose = () => {
    setIsDialogOpen(false);
    couponSelected.current = null;
  }

  const handleDeactivate = async (coupon: ICoupon) => {
    setIsDialogOpen(false);
    try {
      setIxLoading(coupon.id || -1);
      await api.delete(`coupons/${coupon.id}`);
      toast.success("Cupom desativado com sucesso!", toastOptions);
      setRefresh(!refresh);
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    } finally {
      setIxLoading(-1);
    }
  };

  const fetchCoupons = async (page: number, limit: number) => {
    try {
      const data = await api.get("coupons", {
        params: {
          page: page,
          limit: limit,
          idScope: props.idScope,
          scope: props.scope
        }
      });

      setPageNo(page);

      return data;
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });

      return {
        data: { data: [], total: 0 }
      };
    }
  };

  return (
    <div id="page-coupon-list" className="uk-margin-top">
      <TablePage 
        title="Cupons"
        subTitle="Gerencie os cupons de desconto disponíveis."
        messageEmpty={<span>Nenhum <b>cupom</b> encontrado!</span>}
        showAddButton={!authenticationService.isReadOnly()}
        handleAdd={handleNew}
        fetch={fetchCoupons}
        columns={columns}
        pageNo={pageNo}
        refresh={refresh}
      />
      {showAddEdit && !authenticationService.isReadOnly() &&
        <CouponAdd
          open={showAddEdit}
          scope={props.scope}
          idScope={props.idScope}
          coupon={couponEdit}
          onSaved={handleSaved}
          onCancel={handleCancel}
        />
      }
      {!authenticationService.isReadOnly() &&
        <DialogConfirm 
          open={isDialogOpen}
          text={[`Deseja realmente remover o cupom ${couponSelected.current?.code}?`]}
          afterClose={dialogClose}
          handleConfirmation={(confirmationCode) => handleDeactivate(couponSelected.current!)}
        />
      }
    </div>
  );
}
